/**
 * Appointment Validation Utility
 * Centralized validation functions for appointment scheduling
 *
 * IMPORTANT: When modifying this file, update APPOINTMENT_SCHEDULING_FEATURE.md
 */

/**
 * ZIP CODE VALIDATION
 */

// Validate US zip code format and ranges
function isValidUSZip(zipcode) {
    // First check if it's 5 digits
    const zipCodePattern = /^\d{5}$/;
    if (!zipCodePattern.test(zipcode)) {
        return false;
    }

    // Convert to number for range checking
    const zip = parseInt(zipcode, 10);

    // Very permissive validation - only exclude obviously invalid ranges
    // We want to err on the side of accepting valid zip codes rather than rejecting them
    const invalidRanges = [
        [0, 500],       // Not assigned (except 00501-00544 for Holtsville, NY)
        [545, 599],     // Not assigned
        [866, 869],     // Not assigned
        [962, 969],     // Not assigned
        // Removed the 96200-99999 range as it includes valid Alaska zip codes (99xxx)
    ];

    // Special valid ranges in the 00000-00999 range
    const specialValidRanges = [
        [501, 544],     // Holtsville, NY (IRS processing center)
    ];

    // Check special valid ranges first for low numbers
    if (zip < 1000) {
        return specialValidRanges.some(range => zip >= range[0] && zip <= range[1]);
    }

    // For zip codes 01000 and above, accept unless in clearly invalid ranges
    // This includes all state ranges: 01xxx-99xxx
    return !invalidRanges.some(range => zip >= range[0] && zip <= range[1]);
}

// Basic zip code format validation (5 digits only)
function isValidZipFormat(zipcode) {
    return /^\d{5}$/.test(zipcode);
}

// Validate zip code length
function isValidZipLength(zipcode) {
    return zipcode && zipcode.length === 5;
}

// Complete zip code validation (format + ranges)
function validateZipCode(zipcode) {
    const result = {
        isValid: false,
        error: null,
        zipCode: zipcode
    };

    if (!zipcode) {
        result.error = 'Zip code is required';
        return result;
    }

    if (!isValidZipFormat(zipcode)) {
        result.error = 'Zip code must be 5 digits';
        return result;
    }

    if (!isValidUSZip(zipcode)) {
        result.error = 'Invalid zip code range';
        return result;
    }

    result.isValid = true;
    return result;
}

/**
 * INPUT KEY VALIDATION
 */

// Check if key is a numeric digit (0-9)
function isNumericKey(key) {
    return /^[0-9]$/.test(key);
}

// Check if key is allowed for navigation/control
function isAllowedKey(key) {
    const allowedKeys = ['Tab', 'Enter', 'Escape', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'Backspace', 'Delete'];
    return allowedKeys.includes(key);
}

// Check if key is allowed for zip code input
function isValidZipInputKey(key) {
    return isNumericKey(key) || isAllowedKey(key);
}

// Validate keyboard input for zip code fields
function validateZipKeyInput(key, currentValue = '', maxLength = 5) {
    const result = {
        isValid: false,
        shouldPreventDefault: false,
        reason: null
    };

    // Allow control keys
    if (isAllowedKey(key)) {
        result.isValid = true;
        return result;
    }

    // Check if numeric
    if (!isNumericKey(key)) {
        result.shouldPreventDefault = true;
        result.reason = 'Only numeric keys allowed';
        return result;
    }

    // Check length limit
    if (currentValue.length >= maxLength) {
        result.shouldPreventDefault = true;
        result.reason = 'Maximum length reached';
        return result;
    }

    result.isValid = true;
    return result;
}

/**
 * DATE VALIDATION
 */

// Validate date string format (YYYY-MM-DD)
function isValidDateFormat(dateStr) {
    return /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
}

// Validate if date string represents a valid date
function isValidDate(dateStr) {
    if (!isValidDateFormat(dateStr)) {
        return false;
    }

    const date = new Date(dateStr);
    return date instanceof Date && !isNaN(date.getTime());
}

// Validate if date is in the future
function isFutureDate(dateStr) {
    if (!isValidDate(dateStr)) {
        return false;
    }

    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day

    return date >= today;
}

/**
 * APPOINTMENT VALIDATION
 */

// Validate appointment ID
function isValidAppointmentId(appointmentId) {
    return appointmentId && (typeof appointmentId === 'string' || typeof appointmentId === 'number') && appointmentId.toString().length > 0;
}

// Validate appointment data structure
function validateAppointmentData(appointmentData) {
    const result = {
        isValid: false,
        errors: [],
        data: appointmentData
    };

    if (!appointmentData || typeof appointmentData !== 'object') {
        result.errors.push('Appointment data is required');
        return result;
    }

    // Check required fields
    const requiredFields = ['date', 'time', 'location', 'address'];
    requiredFields.forEach(field => {
        if (!appointmentData[field]) {
            result.errors.push(`${field} is required`);
        }
    });

    // Validate appointment ID if present
    if (appointmentData._appointment_id && !isValidAppointmentId(appointmentData._appointment_id)) {
        result.errors.push('Invalid appointment ID');
    }

    result.isValid = result.errors.length === 0;
    return result;
}

/**
 * GENDER VALIDATION
 */

// Validate gender selection
function isValidGender(gender) {
    const validGenders = ['male', 'female', 'other'];
    return validGenders.includes(gender?.toLowerCase());
}

/**
 * GENERAL VALIDATION UTILITIES
 */

// Sanitize input by removing non-numeric characters
function sanitizeNumericInput(input) {
    return input.replace(/[^0-9]/g, '');
}

// Sanitize zip code input
function sanitizeZipCode(input) {
    const sanitized = sanitizeNumericInput(input);
    return sanitized.substring(0, 5); // Limit to 5 digits
}

// Validate required field
function isRequired(value) {
    return value !== null && value !== undefined && value.toString().trim().length > 0;
}

// Validate string length
function isValidLength(value, minLength = 0, maxLength = Infinity) {
    if (!value) return minLength === 0;
    const length = value.toString().length;
    return length >= minLength && length <= maxLength;
}

// Validate against regex pattern
function matchesPattern(value, pattern) {
    if (!value) return false;
    return pattern.test(value.toString());
}

/**
 * VALIDATION RESULT HELPERS
 */

// Create validation result object
function createValidationResult(isValid, error = null, data = null) {
    return {
        isValid,
        error,
        data,
        timestamp: Date.now()
    };
}

// Create validation error result
function createValidationError(error, data = null) {
    return createValidationResult(false, error, data);
}

// Create validation success result
function createValidationSuccess(data = null) {
    return createValidationResult(true, null, data);
}

/**
 * EXPORT FUNCTIONS FOR GLOBAL ACCESS
 */
window.AppointmentValidation = {
    // Zip code validation
    isValidUSZip,
    isValidZipFormat,
    isValidZipLength,
    validateZipCode,
    sanitizeZipCode,

    // Input key validation
    isNumericKey,
    isAllowedKey,
    isValidZipInputKey,
    validateZipKeyInput,

    // Date validation
    isValidDateFormat,
    isValidDate,
    isFutureDate,

    // Appointment validation
    isValidAppointmentId,
    validateAppointmentData,

    // Gender validation
    isValidGender,

    // General utilities
    sanitizeNumericInput,
    isRequired,
    isValidLength,
    matchesPattern,

    // Result helpers
    createValidationResult,
    createValidationError,
    createValidationSuccess
};

// Backward compatibility - expose key functions globally
window.isValidUSZip = isValidUSZip;
window.isNumericKey = isNumericKey;
window.isAllowedKey = isAllowedKey;
window.validateZipCode = validateZipCode;
window.isValidZipFormat = isValidZipFormat;
window.isValidZipLength = isValidZipLength;
window.sanitizeZipCode = sanitizeZipCode;
window.isValidDateFormat = isValidDateFormat;
